/**
 * QuickTestRunCard — walk-the-yard sprinkler test.
 *
 * Lets a user start a test run from any zone and step through sequentially.
 * "Skip" = stop current + start the next zone immediately (no need to wait
 * out the countdown). "Stop" = halts all zones on the controller.
 *
 * Uses the existing Rachio primitives:
 *   GET  /api/rachio/zones             — zone list + device id
 *   POST /api/rachio/zone/start        — start one zone for N seconds
 *   POST /api/rachio/device/stop       — halt everything
 *
 * Self-hides if Rachio isn't connected (stub mode). All state is local to
 * the dialog — no backend state machine needed.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import {
  Zap, Play, SkipForward, Square, Loader2, CheckCircle2, Droplets, Timer,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const DURATIONS = [
  { label: '30s · quick head check', sec: 30 },
  { label: '1 min · coverage walk (default)', sec: 60 },
  { label: '2 min · full-arc verify', sec: 120 },
  { label: '3 min · soak-and-watch', sec: 180 },
];

export const QuickTestRunCard = () => {
  const [open, setOpen] = useState(false);
  const [zones, setZones] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [loadingZones, setLoadingZones] = useState(true);

  const [startZoneIdx, setStartZoneIdx] = useState(0);   // index into zones
  const [perZoneSec, setPerZoneSec] = useState(60);

  const [running, setRunning] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(null);    // index currently firing
  const [remainingSec, setRemainingSec] = useState(0);
  const [status, setStatus] = useState('idle');          // idle|running|done|stopped
  const [pending, setPending] = useState(false);         // busy between zones

  const tickRef = useRef(null);

  // ── Load zones + device on mount ──────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const tok = (typeof window !== 'undefined') ? localStorage.getItem('tp_rachio_token') : null;
        const headers = tok ? { 'X-Rachio-Token': tok } : {};
        const r = await axios.get(`${API_URL}/api/rachio/zones`, { withCredentials: true, headers });
        if (!alive) return;
        const dev = r.data?.device || {};
        setDeviceId(dev.id || null);
        const zs = Array.isArray(dev.zones) ? dev.zones : [];
        zs.sort((a, b) => (a.zone_number || 0) - (b.zone_number || 0));
        setZones(zs);
        setConnected(r.data?.mode === 'live');
      } catch {
        if (alive) setConnected(false);
      } finally {
        if (alive) setLoadingZones(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // ── Timer tick ────────────────────────────────────────────────────
  useEffect(() => {
    if (status !== 'running') return;
    tickRef.current = setInterval(() => {
      setRemainingSec((s) => {
        if (s <= 1) {
          clearInterval(tickRef.current);
          // Auto-advance
          advanceOrFinish();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, currentIdx]);

  // ── Helpers ───────────────────────────────────────────────────────
  const _rachioHeaders = () => {
    const tok = (typeof window !== 'undefined') ? localStorage.getItem('tp_rachio_token') : null;
    return tok ? { 'X-Rachio-Token': tok } : {};
  };

  const stopAll = async () => {
    if (!deviceId) return;
    try {
      await axios.post(
        `${API_URL}/api/rachio/device/stop`,
        { device_id: deviceId },
        { withCredentials: true, headers: _rachioHeaders() },
      );
    } catch {
      // non-fatal — Rachio sometimes 409s if nothing is running
    }
  };

  const startZoneAt = async (idx) => {
    const z = zones[idx];
    if (!z) return;
    setPending(true);
    try {
      await axios.post(
        `${API_URL}/api/rachio/zone/start`,
        { zone_id: z.id, duration_seconds: perZoneSec },
        { withCredentials: true, headers: _rachioHeaders() },
      );
      setCurrentIdx(idx);
      setRemainingSec(perZoneSec);
      setStatus('running');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Zone start failed');
      setStatus('idle');
    } finally {
      setPending(false);
    }
  };

  const advanceOrFinish = async () => {
    // If there's a next zone, start it; else finish
    setCurrentIdx((idx) => {
      const nextIdx = (idx ?? startZoneIdx) + 1;
      if (nextIdx < zones.length) {
        // Kick off next zone (slight delay so Rachio's queue clears cleanly)
        setTimeout(() => startZoneAt(nextIdx), 800);
        return idx;
      } else {
        // Done — stop to be safe
        stopAll();
        setStatus('done');
        setRunning(false);
        return idx;
      }
    });
  };

  const startTest = async () => {
    if (!zones.length) {
      toast.error('No zones found — connect Rachio first');
      return;
    }
    // Always stop anything running first to guarantee a clean start
    await stopAll();
    setRunning(true);
    setStatus('running');
    setCurrentIdx(null);
    await startZoneAt(startZoneIdx);
  };

  const skipToNext = async () => {
    if (currentIdx === null) return;
    clearInterval(tickRef.current);
    setPending(true);
    await stopAll();
    // Advance immediately
    const nextIdx = currentIdx + 1;
    if (nextIdx >= zones.length) {
      setStatus('done');
      setRunning(false);
      setPending(false);
    } else {
      setTimeout(() => startZoneAt(nextIdx), 700);
    }
  };

  const stopTest = async () => {
    clearInterval(tickRef.current);
    await stopAll();
    setStatus('stopped');
    setRunning(false);
    setRemainingSec(0);
  };

  const resetAndClose = () => {
    setOpen(false);
    setStatus('idle');
    setRunning(false);
    setCurrentIdx(null);
    setRemainingSec(0);
  };

  if (!loadingZones && !zones.length) return null;
  if (!connected && zones.length === 0) return null;

  const currentZone = currentIdx !== null ? zones[currentIdx] : null;
  const nextZone = currentIdx !== null ? zones[currentIdx + 1] : null;
  const progress = zones.length ? ((currentIdx ?? -1) + 1) / zones.length : 0;
  const mm = String(Math.floor(remainingSec / 60)).padStart(1, '0');
  const ss = String(remainingSec % 60).padStart(2, '0');

  return (
    <>
      <Card
        data-testid="quick-test-run-card"
        className="border-amber-500/30 bg-gradient-to-r from-amber-50/60 via-background to-background dark:from-amber-950/20"
      >
        <CardContent className="py-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-semibold">
                Walk the yard · Quick Test Run
              </div>
              <div className="font-semibold text-foreground">
                Test every zone, skip forward when you've seen enough
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">
                Step through each zone (30s–3 min) for a quick visual check. To run a real schedule, use Schedules above.
              </div>
            </div>
            <Button
              onClick={() => setOpen(true)}
              className="bg-amber-600 hover:bg-amber-700 shrink-0"
              data-testid="quick-test-run-open-btn"
              disabled={!connected || loadingZones}
            >
              {loadingZones ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              {connected ? 'Test run zones' : 'Connect Rachio first'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { if (!v && running) stopTest(); setOpen(v); }}>
        <DialogContent className="max-w-md" data-testid="quick-test-run-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-600" />
              Quick Test Run
            </DialogTitle>
            <DialogDescription>
              {status === 'idle' && 'Pick a starting zone and per-zone duration. Skip ahead when you\'ve seen enough.'}
              {status === 'running' && 'Running — walk the yard and skip when ready.'}
              {status === 'done' && 'All zones tested.'}
              {status === 'stopped' && 'Test stopped.'}
            </DialogDescription>
          </DialogHeader>

          {/* IDLE — setup form */}
          {status === 'idle' && (
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Start from zone</Label>
                <Select value={String(startZoneIdx)} onValueChange={(v) => setStartZoneIdx(parseInt(v, 10))}>
                  <SelectTrigger data-testid="quick-test-start-zone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {zones.map((z, i) => (
                      <SelectItem key={z.id} value={String(i)}>
                        Zone {z.zone_number} · {z.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Will run through zones {(zones[startZoneIdx]?.zone_number) || '?'}–{zones[zones.length - 1]?.zone_number || '?'} in order.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Per-zone duration</Label>
                <Select value={String(perZoneSec)} onValueChange={(v) => setPerZoneSec(parseInt(v, 10))}>
                  <SelectTrigger data-testid="quick-test-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map((d) => (
                      <SelectItem key={d.sec} value={String(d.sec)}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={startTest}
                disabled={pending || !zones.length}
                className="w-full bg-amber-600 hover:bg-amber-700"
                data-testid="quick-test-start-btn"
              >
                {pending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Start · {zones.length - startZoneIdx} zone{zones.length - startZoneIdx !== 1 ? 's' : ''}
              </Button>
            </div>
          )}

          {/* RUNNING — live controls */}
          {status === 'running' && currentZone && (
            <div className="space-y-4 mt-2" data-testid="quick-test-running">
              <div className="rounded-xl border border-amber-500/30 bg-amber-50/40 dark:bg-amber-950/20 p-4 text-center">
                <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-semibold">
                  Now running · {currentIdx + 1} of {zones.length}
                </div>
                <div className="text-2xl font-bold mt-1">
                  Zone {currentZone.zone_number}
                </div>
                <div className="text-sm text-muted-foreground">{currentZone.name}</div>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <Timer className="w-5 h-5 text-amber-600" />
                  <span className="text-3xl font-mono font-bold text-amber-700 dark:text-amber-300">
                    {mm}:{ss}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="mt-3 h-1.5 rounded-full bg-amber-500/15 overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all"
                    style={{ width: `${Math.round(progress * 100)}%` }}
                  />
                </div>
              </div>

              {nextZone && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Droplets className="w-3.5 h-3.5" />
                  <span>Next: Zone {nextZone.zone_number} · {nextZone.name}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={skipToNext}
                  disabled={pending}
                  variant="outline"
                  data-testid="quick-test-skip-btn"
                >
                  {pending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <SkipForward className="w-4 h-4 mr-2" />}
                  {nextZone ? 'Skip → next' : 'Finish'}
                </Button>
                <Button
                  onClick={stopTest}
                  variant="destructive"
                  data-testid="quick-test-stop-btn"
                >
                  <Square className="w-4 h-4 mr-2" /> Stop test
                </Button>
              </div>
            </div>
          )}

          {/* DONE / STOPPED */}
          {(status === 'done' || status === 'stopped') && (
            <div className="text-center space-y-4 py-4" data-testid="quick-test-finished">
              {status === 'done' ? (
                <>
                  <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
                  <div>
                    <h3 className="text-lg font-semibold">All zones tested ✓</h3>
                    <p className="text-sm text-muted-foreground">
                      Tested {zones.length - startZoneIdx} zone{zones.length - startZoneIdx !== 1 ? 's' : ''} from #{zones[startZoneIdx]?.zone_number} to #{zones[zones.length - 1]?.zone_number}.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Square className="w-12 h-12 mx-auto text-muted-foreground" />
                  <div>
                    <h3 className="text-lg font-semibold">Test stopped</h3>
                    <p className="text-sm text-muted-foreground">Sprinklers halted. Pick up where you left off any time.</p>
                  </div>
                </>
              )}
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={() => { setStatus('idle'); setCurrentIdx(null); }}
                  variant="outline"
                  data-testid="quick-test-again-btn"
                >
                  Run again
                </Button>
                <Button onClick={resetAndClose} data-testid="quick-test-close-btn">
                  <Badge variant="outline" className="mr-1 border-0 p-0">Done</Badge>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickTestRunCard;
